import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: apps } = useQuery({
    queryKey: ['apps'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/apps?limit=5');
      return response;
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/customers?limit=5');
      return response;
    },
  });

  // Fetch install counts per app
  const { data: installsList } = useQuery({
    queryKey: ['installs-list'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/installs-list?limit=1000');
      return response;
    },
  });

  const { data: analytics } = useQuery({
    queryKey: ['analytics-summary'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/analytics');
      return response;
    },
  });

  // Count installs per app
  const installCountByApp = React.useMemo(() => {
    const counts: Record<string, number> = {};
    if (installsList?.items) {
      installsList.items.forEach((install: any) => {
        const appId = install.appId;
        counts[appId] = (counts[appId] || 0) + 1;
      });
    }
    return counts;
  }, [installsList]);

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Total Applications</div>
          <div className="text-3xl font-bold text-gray-900">{apps?.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Total Customers</div>
          <div className="text-3xl font-bold text-gray-900">{customers?.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Linked Installs</div>
          <div className="text-3xl font-bold text-gray-900">{analytics?.totals?.linked_installs ?? 0}</div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Revenue (Paid)</div>
          <div className="text-3xl font-bold text-gray-900">
            ${(((analytics?.totals?.revenue_cents ?? 0) as number) / 100).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Applications</h2>
          <div className="space-y-2">
            {apps?.items?.slice(0, 5).map((app: any) => (
              <button
                key={app.app_id}
                onClick={() => navigate(`/applications/${app.app_id}`)}
                className="w-full text-left flex justify-between items-center py-2 px-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 rounded transition-colors"
              >
                <div className="flex-1">
                  <div className="text-gray-900 font-medium">{app.display_name}</div>
                  <div className="text-xs text-gray-500">
                    {installCountByApp[app.app_id] || 0} install{(installCountByApp[app.app_id] || 0) !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{app.category}</span>
                  <span className="text-blue-600 text-sm">→</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Customers</h2>
          <div className="space-y-2">
            {customers?.items?.slice(0, 5).map((customer: any) => (
              <button
                key={customer.customer_id}
                onClick={() => navigate(`/customers/${customer.customer_id}`)}
                className="w-full text-left flex justify-between items-center py-2 px-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 rounded transition-colors"
              >
                <div className="flex-1">
                  <div className="text-gray-900 font-medium">{customer.name}</div>
                  <div className="text-xs text-gray-500">{customer.email}</div>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">{customer.state}</span>
                  <span className="text-blue-600 text-sm">→</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
